import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { initCsrf } from '@/utils/csrf';
import Home from '@/pages/Home';
import LoginPage from '@pages/Login';
import SignupPage from '@pages/Signup';
import SetPassword from '@pages/SetPassword';
import ResendVerification from '@pages/ResendVerification';
import EmailConfirmation from '@pages/ConfirmEmail';
// import PropertyManagerDashboard from '@components/property_managers/PropertyManagerDashboard';
import { InteractiveViewerPage } from '@/features/interactive-viewer-v2';
import SharedHotspotRedirect from '@/features/interactive-viewer-v2/SharedHotspotRedirect';
import { UnitTourPage } from '@/features/unit-tour';
import { DroneViewer } from '@/features/drones';
import { CreateSurveillanceReport, SurveillanceDashboard } from '@/features/surveillance-reports';
import { ProjectTeam, AcceptInvitation, TradePartnerApplicationPage } from '@/features/project-team';
import PrivateRoute from '@/shared/components/auth/PrivateRoute';
import { Toaster } from '@/shared/ui/toaster';
import CookieConsent from '@/shared/components/CookieConsent';
import ResetPassword from '@pages/ResetPassword';
import ResetPasswordConfirm from '@pages/ResetPasswordConfirm';
import '@styles/theme-variables.css';
import { PropertyProvider } from './context/PropertyContext';
import Dashboard from '@/pages/Dashboard';
import AllProjects from '@/pages/AllProjects';
import AllClients from '@/pages/AllClients';
import ScheduleVisit from '@/pages/ScheduleVisit';
import MyAccount from '@/pages/MyAccount';
import { AddScanPage } from '@/features/add-scan';
import { AddLayoutPage } from '@/features/add-layout';
import { PDFEditor } from '@/features/pdf-editor';
import UploadDronePage from '@/pages/UploadDronePage';
import { useUser } from '@/shared/hooks/useUser';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import { LeadsCapturePage } from '@/features/leads-capture';
import TrainingPage from '@/features/training/components/TrainingPage';
import TrainingManagementPage from '@/features/training/components/TrainingManagementPage';
import {
  PunchListsPage,
  PunchListDetail,
  PunchWorkflowsPage,
  PunchWalkthroughsPage,
} from '@/features/punch-lists';
import { ObservationsPage, ObservationDetail } from '@/features/observations';
import { SafetyIssuesPage, SafetyPage } from '@/features/safety';
import { PhotoGalleryPage } from '@/features/photo-gallery';
import { RfisPage, RfiDetail } from '@/features/rfis';
import { SubmittalsPage, SubmittalDetail } from '@/features/submittals';
import { FilesPage } from '@/features/files';
import { DailyLogPage } from '@/features/daily-log';
import { MeetingsPage, MeetingDetail } from '@/features/meeting-minutes';
import { SupportPage } from '@/features/support/pages/SupportPage';
import PublicSupportPage from '@/features/support/pages/PublicSupportPage';
import PrivacyPolicyPage from '@/features/legal/pages/PrivacyPolicyPage';
import { VersionGate } from '@/shared/hooks/useVersionCheck';
import { TourProvider } from '@/features/tours';
import { ClientAdminPage } from '@/features/roles-and-permissions';
import { ProjectDashboardPage } from '@/features/project-dashboard';
import { ProjectSettingsPage } from '@/features/project-settings';
import { SmartValvesPage } from '@/features/smart-valves';
import ServicesAnnouncementPreview from '@/features/services-announcement/ServicesAnnouncementPreview';

const ErrorHandlingPreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/shared/errors/ErrorHandlingPreview'))
  : null;
const DjangoAppErrorPreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/shared/errors/DjangoAppErrorPreview'))
  : null;
const PunchWalkthroughPreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/features/punch-lists/components/PunchWalkthroughPreview'))
  : null;
const PunchWalkthroughsWorkspacePreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/features/punch-lists/components/PunchWalkthroughsWorkspacePreview'))
  : null;
const LayoutPreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/shared/components/layout/LayoutPreview'))
  : null;

const HeatMapPreview = import.meta.env.VITE_E2E === 'true'
  ? lazy(() => import('@/features/interactive-viewer-v2/layout/HeatMapPreview'))
  : null;

// TODO: Add a route for viewing an individual team member - JC

// Component to handle dashboard redirect
const DashboardRedirect: React.FC = () => {
  const { user, loading } = useUser();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <LogoLoading 
          variant="breathing" 
          size="xl" 
          message="Loading your account..." 
        />
      </div>
    );
  }
  
  if (user?.client_info?.name) {
    return <Navigate to={`/app/dashboard/${user.client_info.name}`} replace />;
  }
  
  // Fallback if no client is found
  return <Navigate to="/" replace />;
};

const LegacyProjectAdministrationRedirect: React.FC = () => {
  const { clientName, projectId } = useParams<{
    clientName?: string;
    projectId: string;
  }>();

  if (!projectId) return <Navigate to="/app" replace />;

  return (
    <Navigate
      to={
        clientName
          ? `/app/property/${clientName}/${projectId}/settings`
          : `/app/property/${projectId}/settings`
      }
      replace
    />
  );
};

function App() {
  // Fetch and store the CSRF token on app mount, exactly as the article prescribes.
  // This runs once regardless of auth state so every subsequent POST/PUT/PATCH/DELETE
  // has the token ready in memory before the user interacts with anything.
  useEffect(() => {
    initCsrf().catch((err) => console.error('Failed to initialise CSRF token:', err));
  }, []);

  return (
    <BrowserRouter>
      <VersionGate />
      <TourProvider>
      <Routes>
        {HeatMapPreview && <Route path="/__e2e__/heat-map"
          element={<Suspense fallback={null}><HeatMapPreview /></Suspense>} />}
        {LayoutPreview && <Route element={<PrivateRoute />}>
          <Route path="/__e2e__/layout/:mode?"
            element={<Suspense fallback={null}><LayoutPreview /></Suspense>} />
        </Route>}
        {PunchWalkthroughsWorkspacePreview && <Route path="/__e2e__/punch-lists/walkthroughs"
          element={<Suspense fallback={null}><PunchWalkthroughsWorkspacePreview /></Suspense>} />}
        {PunchWalkthroughPreview && <Route path="/__e2e__/punch-walkthrough"
          element={<Suspense fallback={null}><PunchWalkthroughPreview /></Suspense>} />}
        {ErrorHandlingPreview && (
          <Route
            path="/__e2e__/errors"
            element={
              <Suspense fallback={null}>
                <ErrorHandlingPreview />
              </Suspense>
            }
          />
        )}
        {DjangoAppErrorPreview && (
          <Route
            path="/__e2e__/api-errors/:appSlug"
            element={
              <Suspense fallback={null}>
                <DjangoAppErrorPreview />
              </Suspense>
            }
          />
        )}
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/accounts/login" element={<Navigate to="/login" replace />} />
        <Route path="/accounts/signup" element={<Navigate to="/signup" replace />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/trade-partner-application" element={<TradePartnerApplicationPage />} />
        <Route path="/shared-hotspot/:accessToken" element={<SharedHotspotRedirect />} />
        <Route path="/unit-tour/:token" element={<UnitTourPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password/:uidb64/:token" element={<ResetPasswordConfirm />} />
        <Route path="/set-password/:token" element={<SetPassword />} />
        <Route path="/verification-sent" element={<ResendVerification />} />
        <Route path="/confirm-email/:key" element={<EmailConfirmation />} />
        <Route path="/services-preview" element={<ServicesAnnouncementPreview />} />
        <Route path="/support" element={<PublicSupportPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        {/* Protected Routes */}
        <Route path="/app" element={<PrivateRoute />}>
          {/* Default dashboard redirect */}
          <Route index element={<DashboardRedirect />} />
          <Route path="dashboard" element={<DashboardRedirect />} />
          
          {/* Dashboard with client name */}
          <Route path="dashboard/:clientName" element={<Dashboard />} />
          
          {/* My Account / Settings */}
          <Route path="account" element={<MyAccount />} />
          
          {/* General protected routes */}
          <Route path="vendors" element={<div>Vendors</div>} />
          
          {/* All Projects page (staff only) */}
          <Route path="projects/all" element={<AllProjects />} />
          
          {/* All Clients page (staff only) */}
          <Route path="clients/all" element={<AllClients />} />
          
          {/* Schedule Visit page */}
          <Route path="schedule-visit" element={<ScheduleVisit />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="smart-valves" element={<SmartValvesPage />} />
          
          {/* Scan routes */}
          <Route path="scan/add" element={<AddScanPage />} />
          
          {/* Layout routes */}
          <Route path="layout/add" element={<AddLayoutPage />} />
          
          {/* Drone routes */}
          <Route path="drone/upload" element={<UploadDronePage />} />
          
          {/* Surveillance routes */}
          <Route path="surveillance/create" element={<CreateSurveillanceReport />} />
          <Route path="surveillance/:clientName/:projectName" element={<SurveillanceDashboard />} />

          {/* Leads capture (staff only) */}
          <Route path="leads/capture" element={<LeadsCapturePage />} />

          {/* Training (client-level) */}
          <Route path="training/:clientName" element={<TrainingPage />} />
          <Route path="training/:clientName/management" element={<TrainingManagementPage />} />
          
          {/* Team routes */}
          <Route path="team/:clientName/:projectName" element={<ProjectTeam />} />

          {/* Client administration (assign project administrators) */}
          <Route path="admin/:clientName" element={<ClientAdminPage />} />
          
          {/* Property-related routes (wrapped with PropertyProvider) */}
          <Route
            path="property/*"
            element={
              <PropertyProvider>
                <Routes>
                  <Route path="documentation" element={<div>Property Documentation</div>} />
                  {/* Legacy routes (no clientName) - kept for bookmark support */}
                  <Route path=":projectId/asbuilds" element={<InteractiveViewerPage />} />
                  <Route path=":projectId/dashboard" element={<ProjectDashboardPage />} />
                  <Route path=":projectId/settings" element={<ProjectSettingsPage />} />
                  <Route
                    path=":projectId/administration"
                    element={<LegacyProjectAdministrationRedirect />}
                  />
                  <Route path=":projectId/smart-valves" element={<SmartValvesPage />} />
                  <Route path=":projectId/progress" element={<InteractiveViewerPage />} />
                  <Route path=":projectId/drones" element={<DroneViewer />} />
                  <Route path=":projectId/drawings" element={<PDFEditor />} />
                  {/* New routes with clientName for sidebar context */}
                  <Route path=":clientName/:projectId/asbuilds" element={<InteractiveViewerPage />} />
                  <Route path=":clientName/:projectId/dashboard" element={<ProjectDashboardPage />} />
                  <Route path=":clientName/:projectId/settings" element={<ProjectSettingsPage />} />
                  <Route
                    path=":clientName/:projectId/administration"
                    element={<LegacyProjectAdministrationRedirect />}
                  />
                  <Route path=":clientName/:projectId/smart-valves" element={<SmartValvesPage />} />
                  <Route path=":clientName/:projectId/progress" element={<InteractiveViewerPage />} />
                  <Route path=":clientName/:projectId/drones" element={<DroneViewer />} />
                  <Route path=":clientName/:projectId/drawings" element={<PDFEditor />} />
                  {/* Punch Lists */}
                  <Route path="punch-lists" element={<PunchListsPage />} />
                  <Route path="punch-lists/workflows" element={<PunchWorkflowsPage />} />
                  <Route path="punch-lists/walkthroughs" element={<PunchWalkthroughsPage />} />
                  <Route path="punch-lists/:id" element={<PunchListDetail />} />
                  <Route path=":projectId/punch-lists" element={<PunchListsPage />} />
                  <Route path=":projectId/punch-lists/workflows" element={<PunchWorkflowsPage />} />
                  <Route path=":projectId/punch-lists/walkthroughs" element={<PunchWalkthroughsPage />} />
                  <Route path=":projectId/punch-lists/:id" element={<PunchListDetail />} />
                  <Route path=":clientName/:projectId/punch-lists" element={<PunchListsPage />} />
                  <Route path=":clientName/:projectId/punch-lists/workflows" element={<PunchWorkflowsPage />} />
                  <Route path=":clientName/:projectId/punch-lists/walkthroughs" element={<PunchWalkthroughsPage />} />
                  <Route path=":clientName/:projectId/punch-lists/:id" element={<PunchListDetail />} />
                  {/* Observations */}
                  <Route path="observations" element={<ObservationsPage />} />
                  <Route path="observations/:id" element={<ObservationDetail />} />
                  <Route path=":projectId/observations" element={<ObservationsPage />} />
                  <Route path=":projectId/observations/:id" element={<ObservationDetail />} />
                  <Route path=":clientName/:projectId/observations" element={<ObservationsPage />} />
                  <Route path=":clientName/:projectId/observations/:id" element={<ObservationDetail />} />
                  {/* Safety */}
                  <Route path="safety" element={<SafetyPage />} />
                  <Route path="safety/issues" element={<SafetyIssuesPage />} />
                  <Route path=":projectId/safety" element={<SafetyPage />} />
                  <Route path=":projectId/safety/issues" element={<SafetyIssuesPage />} />
                  <Route path=":clientName/:projectId/safety" element={<SafetyPage />} />
                  <Route path=":clientName/:projectId/safety/issues" element={<SafetyIssuesPage />} />
                  {/* RFIs */}
                  <Route path="rfis" element={<RfisPage />} />
                  <Route path="rfis/:id" element={<RfiDetail />} />
                  <Route path=":projectId/rfis" element={<RfisPage />} />
                  <Route path=":projectId/rfis/:id" element={<RfiDetail />} />
                  <Route path=":clientName/:projectId/rfis" element={<RfisPage />} />
                  <Route path=":clientName/:projectId/rfis/:id" element={<RfiDetail />} />
                  {/* Submittals */}
                  <Route path="submittals" element={<SubmittalsPage />} />
                  <Route path="submittals/:id" element={<SubmittalDetail />} />
                  <Route path=":projectId/submittals" element={<SubmittalsPage />} />
                  <Route path=":projectId/submittals/:id" element={<SubmittalDetail />} />
                  <Route path=":clientName/:projectId/submittals" element={<SubmittalsPage />} />
                  <Route path=":clientName/:projectId/submittals/:id" element={<SubmittalDetail />} />
                  {/* Photo Gallery */}
                  <Route path="photos" element={<PhotoGalleryPage />} />
                  <Route path=":projectId/photos" element={<PhotoGalleryPage />} />
                  <Route path=":clientName/:projectId/photos" element={<PhotoGalleryPage />} />
                  {/* Files */}
                  <Route path="files" element={<FilesPage />} />
                  <Route path="files/folder/:folderId" element={<FilesPage />} />
                  <Route path=":projectId/files" element={<FilesPage />} />
                  <Route path=":projectId/files/folder/:folderId" element={<FilesPage />} />
                  <Route path=":clientName/:projectId/files" element={<FilesPage />} />
                  <Route path=":clientName/:projectId/files/folder/:folderId" element={<FilesPage />} />
                  {/* Daily Log */}
                  <Route path="daily-log" element={<DailyLogPage />} />
                  <Route path=":projectId/daily-log" element={<DailyLogPage />} />
                  <Route path=":clientName/:projectId/daily-log" element={<DailyLogPage />} />
                  {/* Meeting Minutes */}
                  <Route path="meeting-minutes" element={<MeetingsPage />} />
                  <Route path="meeting-minutes/:id" element={<MeetingDetail />} />
                  <Route path=":projectId/meeting-minutes" element={<MeetingsPage />} />
                  <Route path=":projectId/meeting-minutes/:id" element={<MeetingDetail />} />
                  <Route path=":clientName/:projectId/meeting-minutes" element={<MeetingsPage />} />
                  <Route path=":clientName/:projectId/meeting-minutes/:id" element={<MeetingDetail />} />
                </Routes>
              </PropertyProvider>
            }
          />
        </Route>

        {/* Catch all route for 404s */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </TourProvider>
      <Toaster />
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
